import posthog from 'posthog-js';



const POSTHOG_KEY = 'phc_yN643ELs4Fauzc8GqeFy9rbKDXbHcJFg3iEKcQjK2g3K';

// En dev (Vite) : proxy /ingest — en prod/prerender : reverse proxy PostHog

const POSTHOG_HOST = import.meta.env.DEV

  ? `${window.location.origin}/ingest`

  : 'https://t.focuspips.com';



/**
 * Seuls les hôtes de prod alimentent le projet PostHog (allowlist). L'ancienne denylist
 * (localhost / 127.0.0.1 / 192.168.*) laissait passer les serveurs Vite exposés en LAN ou
 * Tailscale (`focuspips-server:3000`, `*.ts.net`) : des sessions de dev comptées comme
 * visiteurs réels (audit 2026-09-20). Même logique d'hôtes de dev que src/config/api.ts.
 * Note : la WebView Capacitor Android sert depuis `localhost` — elle est donc hors tracking,
 * comme avant ; à revoir si l'app Android est distribuée.
 */
const PROD_ANALYTICS_HOSTS = ['focuspips.com', 'www.focuspips.com'];

function isLocalAnalyticsHost(): boolean {
  if (typeof window === 'undefined') return true;
  return !PROD_ANALYTICS_HOSTS.includes(window.location.hostname);
}

export function initPostHog() {
  if (isLocalAnalyticsHost()) return;

  posthog.init(POSTHOG_KEY, {

    api_host: POSTHOG_HOST,

    ui_host: 'https://us.posthog.com',

    person_profiles: 'always',

    // Rien ne part tant que PostHogTracker n'a pas tranché le mode. posthog-js est opt-in
    // par défaut : sans ça, un abonné payant sur un navigateur neuf envoie $pageleave /
    // $exception pendant le chargement du profil Firestore, avant tout opt-out.
    // (opt_out_persistence_by_default reste false : register() / super-properties continuent
    // de fonctionner hors opt-in, cf. registerAcquisitionParams.)
    opt_out_capturing_by_default: true,

    capture_pageview: false,

    capture_pageleave: true,

    // Replay désactivé par défaut — démarré uniquement pour un essai en cours (PostHogTracker)

    disable_session_recording: true,

    session_recording: {

      maskAllInputs: true,

      maskInputOptions: { password: true },

    },

    autocapture: false,
    capture_exceptions: true,

    disable_heatmaps: true,

    loaded: () => {
      // debug() désactivé — spam console + session recorder en boucle en dev
    },

  });

}



/**
 * Les trois seuls régimes de tracking possibles :
 * - `trial`  : events + session replay — RÉSERVÉ à un essai en cours (le seul cas qui
 *              consomme du quota replay).
 * - `events` : events seuls, aucun replay — visiteurs non connectés (funnel d'acquisition)
 *              et offre Basic gratuite (funnel d'upsell Basic → Premium).
 * - `off`    : rien du tout — abonnés payants, comptes internes, tout le reste.
 */
export type PostHogTrackingMode = 'trial' | 'events' | 'off';

/**
 * Point d'entrée UNIQUE du tracking : c'est la seule fonction qui a le droit de démarrer
 * un replay. Elle coupe explicitement l'enregistrement dans les deux autres modes, donc
 * aucun chemin de code ne peut laisser un replay ouvert sur un non-trial.
 * Le mode est décidé par PostHogTracker (src/components/PostHogTracker.tsx).
 */
export function applyPostHogTrackingMode(mode: PostHogTrackingMode) {
  if (isLocalAnalyticsHost()) return;

  if (mode === 'off') {
    posthog.stopSessionRecording();
    posthog.opt_out_capturing();
    return;
  }

  // captureEventName: false — sinon posthog-js émet un `$opt_in` facturé à chaque appel
  // (PostHogTracker en fait un par chargement de page : 7 100 events/mois pour rien).
  posthog.opt_in_capturing({ captureEventName: false });

  if (mode === 'trial') {
    posthog.startSessionRecording();
  } else {
    posthog.stopSessionRecording();
  }
}



/**

 * Offre Basic (gratuite). Deux chemins serveur y mènent (cronSubscriptions) :

 * fin d'essai → subscriptionType 'Mensuel Basic' (isPremium true, isTrial true),

 * et fin d'abonnement payant → subscriptionType 'basic' (isPremium false).

 * Le seul dénominateur commun fiable est donc le subscriptionType.

 */

export function isBasicSubscriptionType(subscriptionType?: string | null): boolean {

  return !!subscriptionType && subscriptionType.toLowerCase().includes('basic');

}



export function identifyUser(params: {

  uid: string;

  email?: string | null;

  displayName?: string | null;

  isPremium: boolean;

  isTrial: boolean;

  isHomePromo: boolean;

  isBasic?: boolean;

  subscriptionType: string;

  createdAt?: Date | null;

}) {
  if (isLocalAnalyticsHost()) return;

  posthog.identify(params.uid, {

    email: params.email ?? undefined,

    name: params.displayName ?? undefined,

    is_premium: params.isPremium,

    is_trial: params.isTrial,

    is_home_promo: params.isHomePromo,

    is_basic: params.isBasic ?? isBasicSubscriptionType(params.subscriptionType),

    subscription_type: params.subscriptionType || 'free',

    is_internal_user: isInternalPostHogEmail(params.email),

    created_at: params.createdAt?.toISOString() ?? undefined,

  });

}



export function resetUser() {

  posthog.reset();

}



// Miroir de proxy-server/services/posthog.js INTERNAL_EMAIL_PATTERNS et du cohort PostHog
// « Internal / Test users » (341114) : les trois doivent rester alignés.
const INTERNAL_POSTHOG_EMAIL_PATTERNS = [
  /@focuspips\.com$/i,
  /@tradetracker\.com$/i,
  /^corentinlehuerou(?:\+[^@]+)?@gmail\.com$/i,
  /^holocubics(?:\+[^@]+)?@gmail\.com$/i, // comptes de test (paiements Stripe test, beta)
];

export function isInternalPostHogEmail(email?: string | null): boolean {
  if (!email) return false;
  return INTERNAL_POSTHOG_EMAIL_PATTERNS.some(pattern => pattern.test(email.trim()));
}



export function capturePageView(path: string) {
  if (isLocalAnalyticsHost()) return;

  posthog.capture('$pageview', { $current_url: window.location.origin + path });

}



export function capture(
  event: string,
  properties?: Record<string, unknown>,
  options?: {
    /** Envoi immédiat hors file d'attente — pour un event émis juste avant un opt-out (fin d'upgrade). */
    sendInstantly?: boolean;
  },
) {
  if (isLocalAnalyticsHost()) return;

  posthog.capture(event, properties, options?.sendInstantly ? { send_instantly: true } : undefined);

}

/** Super-properties persistées (localStorage) et collées à tous les events suivants. */
export function registerSuperProperties(props: Record<string, string | number | boolean>) {
  if (isLocalAnalyticsHost()) return;
  try {
    posthog.register(props);
  } catch {
    // Le tracking ne doit jamais casser l'app.
  }
}

export function captureException(error: unknown, properties?: Record<string, unknown>) {
  if (isLocalAnalyticsHost()) return;
  posthog.captureException(error, properties);
}



/**
 * Enregistre les UTM/click-ids de l'URL courante comme "super properties" PostHog,
 * AVANT que les pages d'atterrissage ne nettoient l'URL via history.replaceState.
 * posthog.register() persiste ces valeurs immédiatement (localStorage), indépendamment
 * de l'opt-in de capture — elles seront jointes à tous les events envoyés ensuite.
 */

export function registerAcquisitionParams(fallbackSource?: string, forceSource = false) {

  try {

    const params = new URLSearchParams(window.location.search);

    const props: Record<string, string> = {};

    const utmSource = forceSource ? fallbackSource : (params.get('utm_source') || fallbackSource);

    if (utmSource) props.utm_source = utmSource;

    const utmMedium = params.get('utm_medium');

    if (utmMedium) props.utm_medium = utmMedium;

    const utmCampaign = params.get('utm_campaign');

    if (utmCampaign) props.utm_campaign = utmCampaign;

    const utmTerm = params.get('utm_term');

    if (utmTerm) props.utm_term = utmTerm;

    const utmContent = params.get('utm_content');

    if (utmContent) props.utm_content = utmContent;

    const fbclid = params.get('fbclid');

    if (fbclid) props.fbclid = fbclid;

    const gclid = params.get('gclid');

    if (gclid) props.gclid = gclid;

    if (document.referrer) props.initial_referrer = document.referrer;

    if (Object.keys(props).length > 0) posthog.register(props);

  } catch {

    // silently ignore

  }

}



export { posthog };


